import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Layout from '@components/layout';
import Breadcrumb from '@components/breadcrumb';
import { styles } from '@components/utils';
import breakpoints from '@rsc/ui-components/breakpoints';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';
import Articles from '@components/articles/Articles';
import PopularTopics from '@components/articles/PopularTopics';
import Pagination from '@components/pagination';
import createOpenGraphTags from '@rsc/utils/createOpenGraphTags';

const PageContainer = styled.div`
  background-color: ${styles.colors.gray[100]};
`;

const HeaderImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  background: url(/images/articles/blog-title-bg.jpg) no-repeat;
  background-position: center;
  background-size: cover;

  @media (min-width: ${breakpoints.md}) {
    height: 160px;
  }

  @media (min-width: ${breakpoints.lg}) {
    height: 310px;
  }

  @media (min-width: ${breakpoints.xl}) {
    height: 380px;
  }
`;

const PageTitle = styled.div`
  font-size: ${styles.fontSize['1xl']};
  color: #fff;
  font-weight: ${styles.fontWeight.bold};
  text-transform: uppercase;
  @media (min-width: ${breakpoints.md}) {
    font-size: ${styles.fontSize['2xl']};
  }
  @media (min-width: ${breakpoints.lg}) {
    font-size: ${styles.fontSize['4xl']};
  }
`;

const ArticleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 0;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 50px 0;
  }
`;

const BreadcrumbContainer = styled.div`
  width: auto;
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
`;

const Container = styled.div`
  margin: 0 25px;
  padding: 20px;
  background: #ffffff;
  @media (min-width: ${breakpoints.lg}) {
    padding: 25px 75px;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${styles.fontSize.lg};
  margin-top: 0;
  margin-bottom: 30px;
  text-transform: capitalize;
  color: ${styles.colors.blue[100]};
  font-weight: ${styles.fontWeight.bold};

  @media (min-width: ${breakpoints.md}) {
    font-size: ${styles.fontSize['1xl']};
    margin-bottom: 50px;
  }

  @media (min-width: ${breakpoints.lg}) {
    font-size: ${styles.fontSize['3xl']};
  }
`;

const ArticleCategoryListing = ({ data, pageContext }) => {
  const articles = data.allShopifyBlogPost.nodes;
  const { numPages, currentPage, breadcrumbs, tag } = pageContext;

  const FormatTag = blogTag => blogTag.replace(/ /g, '+');

  return (
    <Layout
      headTagData={{
        title: 'Blog | Kurgo Dog Products',
        keywords:
          'Dog travel, dog outdoors, dog training, dog trip, dog car, dog hike',
        meta: createOpenGraphTags(
          {
            title: 'Blog | Kurgo Dog Products',
            url: `/blog/tag/${FormatTag(tag)}`,
          },
          'kurgo'
        ),
      }}
    >
      <PageContainer>
        <BreadcrumbContainer>
          {breadcrumbs && <Breadcrumb elements={breadcrumbs} />}
        </BreadcrumbContainer>
        <HeaderImage>
          <PageTitle>Blog</PageTitle>
        </HeaderImage>
        <ArticleWrapper>
          <PageWrapper>
            <Container>
              <SectionTitle>{tag}</SectionTitle>
              <Articles articles={articles} />
              <Pagination
                numPages={numPages}
                currentPage={currentPage}
                baseUrl={`blog/tag/${FormatTag(tag)}`}
              />
              <SectionTitle>Popular Topics</SectionTitle>
              <PopularTopics />
            </Container>
          </PageWrapper>
        </ArticleWrapper>
      </PageContainer>
    </Layout>
  );
};

ArticleCategoryListing.propTypes = {
  data: PropTypes.shape({
    allShopifyBlogPost: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          url: PropTypes.string,
          excerpt: PropTypes.string,
          image: PropTypes.shape({
            src: PropTypes.string,
            altText: PropTypes.string,
          }),
          publishedAt: PropTypes.string,
          tags: PropTypes.arrayOf(PropTypes.string),
        })
      ),
    }),
  }).isRequired,
  pageContext: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
    numPages: PropTypes.number,
    currentPage: PropTypes.number,
    tag: PropTypes.string,
  }).isRequired,
};

export default ArticleCategoryListing;

export const pageQuery = graphql`
  query articleCategoryPageQuery($skip: Int!, $limit: Int!, $tag: String!) {
    allShopifyBlogPost(
      sort: { publishedAt: DESC }
      limit: $limit
      skip: $skip
      filter: { tags: { eq: $tag } }
    ) {
      nodes {
        title
        handle
        excerpt
        tags
        publishedAt
        image {
          url
          altText
        }
      }
    }
  }
`;
